import { Modal, useModals } from 'almanac-ui/Modal'
import Styles from './SourceMaterialModal.module.scss'
import { Tabs } from 'components/BlazeTabs/Tabs'
import { Item } from '@react-stately/collections'
import { Importer } from './ContentSourcePicker/Importer'
import { useEffect, useRef } from 'react'
import { KnowledgeBaseSourceMaterial } from 'types'
import { ScrapeUrlClient } from 'api/clients/ScrapeUrlClient'
import { SourcePreviewModal } from 'screens/BrandKits/components/SourceMaterialModal/SourcePreviewModal'
import { Processing } from './Processing/Processing'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { MobileHeader } from './MobileHeader'
import { CONTENT_CHAR_LIMIT } from 'screens/BrandKits/components/SourceMaterialModal/SourceMaterialModal'
import {
  CreateKnowledgeBaseSourceMaterialPayload,
  UpdateKnowledgeBaseSourceMaterialPayload,
} from 'api/clients/KnowledgeBaseClient'
import { useAsyncScreenshot } from 'hooks/queries/useAsyncScreenshot'
import { reportError } from 'utils/error'

export interface Props {
  onHandleSourceMaterialSuccess: (sourceMaterial: KnowledgeBaseSourceMaterial) => void
  sourceMaterial: KnowledgeBaseSourceMaterial & { source_url: string }
  onError?: () => void
}

export const AnalysingModal = ({ onHandleSourceMaterialSuccess, sourceMaterial, onError }: Props) => {
  const { closeModal, openModal } = useModals()
  const abortController = useRef(new AbortController())
  const { createScreenshot } = useAsyncScreenshot()

  useEffect(() => {
    const scrapeContent = async () => {
      try {
        const [content, screenshot] = await Promise.all([
          ScrapeUrlClient.scrape(sourceMaterial.source_url, abortController.current.signal),
          createScreenshot(sourceMaterial.source_url, abortController.current.signal),
        ])

        if (!content) {
          onError?.()
          closeModal()
          return
        }

        openModal(SourcePreviewModal, {
          onHandleSourceMaterialSuccess,
          sourceMaterial: { ...sourceMaterial, screenshot, content: content.substring(0, CONTENT_CHAR_LIMIT) } as
            | UpdateKnowledgeBaseSourceMaterialPayload
            | CreateKnowledgeBaseSourceMaterialPayload,
        })
      } catch (e) {
        reportError(e)
        onError?.()
        closeModal()
      }
    }

    scrapeContent()

    return () => {
      abortController.current.abort()
      abortController.current = new AbortController()
    }
  }, [sourceMaterial.source_url]) // eslint-disable-line react-hooks/exhaustive-deps

  const cancelRequest = () => {
    abortController.current.abort()
    closeModal()
  }

  return (
    <Modal.Root
      size="sm"
      blaze
      onClose={() => {
        abortController.current.abort()
        closeModal()
      }}
      onPressOutside={() => {
        abortController.current.abort()
        closeModal()
      }}
      bodyClassName={Styles.modal}
      aria-label="Analyzing Source"
    >
      <ResponsiveDesign.Desktop>
        <Processing onCancel={cancelRequest} />
      </ResponsiveDesign.Desktop>
      <ResponsiveDesign.Mobile>
        <MobileHeader
          onBack={() => {
            abortController.current.abort()
            closeModal()
          }}
        >
          {sourceMaterial ? 'Update' : 'Create'} source material
        </MobileHeader>
        <div className={Styles.container}>
          <div className={Styles.tabs}>
            <Tabs withEditorTopColor aria-label="Source material options" animate classNames={{ tab: Styles.tab }}>
              <Item
                textValue="Link Website"
                key={Importer.ScanWebsite}
                title={
                  <div className={Styles.tabContents}>
                    <span>Link Website</span>
                  </div>
                }
              >
                <Processing onCancel={cancelRequest} />
              </Item>
              <Item
                textValue="Paste Text"
                key={Importer.AddText}
                title={
                  <div className={Styles.tabContents}>
                    <span>Paste Text</span>
                  </div>
                }
              >
                <Processing onCancel={cancelRequest} />
              </Item>
              <Item
                textValue="Select File"
                key={Importer.SelectDoc}
                title={
                  <div className={Styles.tabContents}>
                    <span>Select File</span>
                  </div>
                }
              >
                <Processing onCancel={cancelRequest} />
              </Item>
              <Item
                textValue="Upload File"
                key={Importer.UploadFiles}
                title={
                  <div className={Styles.tabContents}>
                    <span>Upload File</span>
                  </div>
                }
              >
                <Processing onCancel={cancelRequest} />
              </Item>
            </Tabs>
          </div>
        </div>
      </ResponsiveDesign.Mobile>
    </Modal.Root>
  )
}
